import { Badge } from "design-system/atoms/Badge"
import { Skeleton } from "design-system/atoms/Skeleton"
import { Switch } from "design-system/atoms/Switch"
import { Card } from "design-system/molecules/Card"
import { NoData } from "design-system/molecules/NoData"
import {
  FlagsTreemapChart,
  FlagsTreemapTooltipData,
} from "modules/dashboard/components/Treemaps/FlagsTreemapChart"
import { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import {
  BackofficeFlagsUnnamespacedNamespace,
  GetBackofficeFlagsTreemapResponseDataSchema,
} from "schema"
import { z } from "zod"
import {
  BackofficeFlagsCompanyTreemapChart,
  FlagsCompanyTreemapTooltipData,
} from "./BackofficeFlagsCompanyTreemapChart"

type TreemapData = z.infer<typeof GetBackofficeFlagsTreemapResponseDataSchema>

type Props = {
  data: TreemapData | undefined
  isPending: boolean
  isCompanyMode: boolean
  onCompanyClick: (company: { id: string; name: string }) => void
  onCompanyModeChange: (isCompanyMode: boolean) => void
  onNamespaceClick: (namespace: string) => void
  onKeyClick: (key: string) => void
  selectedCompanyName: string | undefined
  selectedKeys: string[]
}

export function BackofficeFlagsTreemap({
  data,
  isPending,
  isCompanyMode,
  onCompanyClick,
  onCompanyModeChange,
  onNamespaceClick,
  onKeyClick,
  selectedCompanyName,
  selectedKeys,
}: Props) {
  const { t } = useTranslation()
  const [tooltip, setTooltip] = useState<
    FlagsTreemapTooltipData | FlagsCompanyTreemapTooltipData | null
  >(null)
  const hasData =
    !!data &&
    (data.groupBy === "key"
      ? data.namespaces.length > 0
      : data.companies.some(({ count }) => count >= 20))
  const getNamespaceLabel = useCallback(
    (namespace: string) =>
      namespace === BackofficeFlagsUnnamespacedNamespace
        ? t("backoffice.flags.treemap.unnamespaced")
        : namespace,
    [t],
  )

  useEffect(() => setTooltip(null), [data, isPending])

  const tooltipLeft =
    typeof window === "undefined" || !tooltip
      ? 0
      : Math.max(8, Math.min(tooltip.coordinate.x, window.innerWidth - 260))
  const tooltipTop =
    typeof window === "undefined" || !tooltip
      ? 0
      : Math.max(8, Math.min(tooltip.coordinate.y, window.innerHeight - 160))

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center justify-between gap-4">
            <span className="text-md font-medium">{t("backoffice.flags.treemap.title")}</span>
            <div className="flex flex-wrap items-center justify-end gap-6">
              <div className="flex items-center gap-2">
                {isCompanyMode &&
                  selectedKeys.map((key) => (
                    <Badge key={key} variant="outline">
                      {key}
                    </Badge>
                  ))}
                {!isCompanyMode && selectedCompanyName && (
                  <Badge variant="outline">{selectedCompanyName}</Badge>
                )}
              </div>
              <Switch checked={isCompanyMode} onCheckedChange={onCompanyModeChange} />
            </div>
          </div>
          <span className="text-sm text-muted-foreground">
            {t(
              isCompanyMode
                ? "backoffice.flags.treemap.companyDescription"
                : "backoffice.flags.treemap.description",
            )}
          </span>
        </div>
        <div className="relative h-[280px]">
          {isPending && <Skeleton className="h-full w-full" />}
          {!isPending &&
            hasData &&
            data &&
            (data.groupBy === "key" ? (
              <FlagsTreemapChart
                data={data.namespaces}
                getNamespaceLabel={getNamespaceLabel}
                onNamespaceClick={onNamespaceClick}
                onKeyClick={onKeyClick}
                onTooltipChange={setTooltip}
              />
            ) : (
              <BackofficeFlagsCompanyTreemapChart
                data={data.companies}
                onCompanyClick={onCompanyClick}
                onTooltipChange={setTooltip}
              />
            ))}
          {!isPending && !hasData && (
            <NoData
              frame
              className="h-full"
              title={t("backoffice.flags.treemap.emptyTitle")}
              description={t("backoffice.flags.treemap.emptyDescription")}
            />
          )}
        </div>
      </div>
      {typeof document !== "undefined" && tooltip
        ? createPortal(
            <Card
              className="pointer-events-none fixed z-50 min-w-56 p-3 shadow-lg"
              style={{ left: tooltipLeft, top: tooltipTop }}
            >
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                {"namespaceLabel" in tooltip && (
                  <>
                    <span className="text-muted-foreground">
                      {t("backoffice.flags.treemap.tooltip.namespace")}
                    </span>
                    <span className="font-medium">{tooltip.namespaceLabel}</span>
                    <span className="text-muted-foreground">
                      {t("backoffice.flags.treemap.tooltip.key")}
                    </span>
                    <span className="font-medium">{tooltip.key}</span>
                    <span className="text-muted-foreground">
                      {t("backoffice.flags.treemap.tooltip.kind")}
                    </span>
                    <span className="font-medium">{tooltip.kind}</span>
                  </>
                )}
                {"companyName" in tooltip && (
                  <>
                    <span className="text-muted-foreground">
                      {t("backoffice.flags.treemap.tooltip.company")}
                    </span>
                    <span className="font-medium">{tooltip.companyName}</span>
                    <span className="text-muted-foreground">
                      {t("backoffice.flags.treemap.tooltip.rate")}
                    </span>
                    <span className="font-medium">
                      {`${((tooltip.rate ?? 0) * 100).toFixed(1)}%`}
                    </span>
                  </>
                )}
                <span className="text-muted-foreground">
                  {t("backoffice.flags.treemap.tooltip.count")}
                </span>
                <span className="font-medium">{tooltip.count}</span>
              </div>
            </Card>,
            document.body,
          )
        : null}
    </Card>
  )
}
